import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  BadgeCheck,
  ChevronDown,
  ChevronUp,
  Eye,
  EyeOff,
  Github,
  Link2,
  Linkedin,
  Loader2,
  Share2,
  Trash2,
  Twitter,
} from 'lucide-react';
import { apiService } from '../services/apiService';

export interface SocialProfile {
  platform: 'linkedin' | 'github' | 'twitter' | 'facebook' | 'instagram';
  profileUrl: string;
  username: string;
  isVerified: boolean;
  profileData?: any;
  privacyLevel: 'public' | 'partial' | 'private';
}

interface SocialPost {
  id: string;
  text: string;
  url?: string;
  createdAt: string;
  source: string;
}

interface Props {
  onProfilesChange?: (profiles: SocialProfile[]) => void;
}

const PLATFORM_META: Record<string, { label: string; icon: React.ReactNode; placeholder: string }> = {
  github: { label: 'GitHub', icon: <Github className="w-4 h-4" />, placeholder: 'https://github.com/you' },
  linkedin: { label: 'LinkedIn', icon: <Linkedin className="w-4 h-4" />, placeholder: 'https://linkedin.com/in/you' },
  twitter: { label: 'X / Twitter', icon: <Twitter className="w-4 h-4" />, placeholder: 'https://x.com/you' },
  instagram: { label: 'Instagram', icon: <Share2 className="w-4 h-4" />, placeholder: 'https://instagram.com/you' },
};

const SocialConnectionsCard: React.FC<Props> = ({ onProfilesChange }) => {
  const [profiles, setProfiles] = useState<SocialProfile[]>([]);
  const [providers, setProviders] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState<string | null>(null); // platform link-form open for
  const [inputValue, setInputValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [startingOAuth, setStartingOAuth] = useState<string | null>(null);
  const [postsFor, setPostsFor] = useState<string | null>(null);
  const [posts, setPosts] = useState<SocialPost[]>([]);
  const [postsLoading, setPostsLoading] = useState(false);

  const setAndNotify = (next: SocialProfile[]) => {
    setProfiles(next);
    onProfilesChange?.(next);
  };

  useEffect(() => {
    (async () => {
      try {
        const [profilesRes, providersRes] = await Promise.all([
          apiService.get('social'),
          apiService.get('social/oauth/providers'),
        ]);
        setAndNotify(profilesRes.data.data || []);
        setProviders(providersRes.data.data || {});
      } catch {
        /* keep empty state */
      } finally {
        setLoading(false);
      }
    })();

    // Result of a returning OAuth redirect (?social_connected= / ?social_error=)
    const params = new URLSearchParams(window.location.search);
    const connected = params.get('social_connected');
    const oauthError = params.get('social_error');
    if (connected || oauthError) {
      if (connected) {
        toast.success(`${PLATFORM_META[connected]?.label || connected} verified and connected!`);
      } else if (oauthError) {
        toast.error(oauthError);
      }
      params.delete('social_connected');
      params.delete('social_error');
      const query = params.toString();
      window.history.replaceState({}, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Verified connect: sign in with the platform to prove account ownership. */
  const startOAuth = async (platform: string) => {
    setStartingOAuth(platform);
    try {
      const res = await apiService.get(`social/oauth/${platform}/start`);
      window.location.href = res.data.data.url;
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Could not start sign-in.');
      setStartingOAuth(null);
    }
  };

  /** Fallback: attach a profile URL — shown as an unverified link. */
  const linkByUrl = async (platform: string) => {
    if (!inputValue.trim()) return;
    setSubmitting(true);
    try {
      const res = await apiService.post('social/link', { platform, profileUrl: inputValue.trim() });
      const profile: SocialProfile = res.data.data;
      setAndNotify([...profiles.filter(p => p.platform !== platform), profile]);
      setAdding(null);
      setInputValue('');
      toast.success(`${PLATFORM_META[platform].label} linked (unverified).`);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Could not link. Check the URL and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const disconnect = async (platform: string) => {
    try {
      await apiService.delete(`social/${platform}`);
      setAndNotify(profiles.filter(p => p.platform !== platform));
      if (postsFor === platform) setPostsFor(null);
      toast.success('Disconnected.');
    } catch {
      toast.error('Could not disconnect.');
    }
  };

  const togglePrivacy = async (profile: SocialProfile) => {
    const next = profile.privacyLevel === 'public' ? 'private' : 'public';
    try {
      await apiService.patch(`social/${profile.platform}/privacy`, { privacyLevel: next });
      setAndNotify(profiles.map(p => (p.platform === profile.platform ? { ...p, privacyLevel: next } : p)));
    } catch {
      toast.error('Could not update privacy.');
    }
  };

  const togglePosts = async (platform: string) => {
    if (postsFor === platform) {
      setPostsFor(null);
      return;
    }
    setPostsFor(platform);
    setPostsLoading(true);
    try {
      const res = await apiService.get(`social/${platform}/posts`);
      setPosts(res.data.data || []);
    } catch {
      setPosts([]);
    } finally {
      setPostsLoading(false);
    }
  };

  const hasReadableActivity = (profile: SocialProfile) =>
    profile.platform === 'github' || (profile.isVerified && profile.platform === 'twitter');

  const availablePlatforms = Object.keys(PLATFORM_META).filter(
    p => !profiles.some(profile => profile.platform === p)
  );

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-center gap-2 mb-3">
        <Link2 className="w-5 h-5 text-indigo-600" />
        <h3 className="font-semibold text-gray-900">Social profiles</h3>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-gray-500 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : (
        <>
          {profiles.length === 0 && (
            <p className="text-sm text-gray-600 mb-3">
              Sign in with your accounts to prove they're yours — verified profiles carry far more
              weight with employers.
            </p>
          )}

          <div className="space-y-2">
            {profiles.map(profile => (
              <div key={profile.platform} className="border border-gray-100 rounded-lg">
                <div className="flex items-center gap-2 p-2.5">
                  <span className="text-gray-700">{PLATFORM_META[profile.platform]?.icon || <Share2 className="w-4 h-4" />}</span>
                  <a
                    href={profile.profileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-medium text-gray-900 hover:text-indigo-600 truncate"
                  >
                    @{profile.username}
                  </a>
                  {profile.isVerified ? (
                    <span
                      title="Ownership verified via platform sign-in"
                      className="flex items-center gap-0.5 text-xs font-medium text-green-700 bg-green-50 px-1.5 py-0.5 rounded-full flex-shrink-0"
                    >
                      <BadgeCheck className="w-3.5 h-3.5" /> Verified
                    </span>
                  ) : (
                    <span
                      title="Link only — ownership not verified"
                      className="text-xs font-medium text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded-full flex-shrink-0"
                    >
                      Unverified
                    </span>
                  )}
                  <div className="ml-auto flex items-center gap-1">
                    <button
                      onClick={() => togglePrivacy(profile)}
                      title={profile.privacyLevel === 'public' ? 'Visible to employers — click to hide' : 'Hidden from employers — click to show'}
                      className="p-1.5 rounded hover:bg-gray-100 text-gray-500"
                    >
                      {profile.privacyLevel === 'public' ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </button>
                    {hasReadableActivity(profile) && (
                      <button
                        onClick={() => togglePosts(profile.platform)}
                        title="Recent activity"
                        className="p-1.5 rounded hover:bg-gray-100 text-gray-500"
                      >
                        {postsFor === profile.platform ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                    )}
                    <button
                      onClick={() => disconnect(profile.platform)}
                      title="Disconnect"
                      className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {!profile.isVerified && providers[profile.platform] && (
                  <div className="border-t border-gray-100 px-3 py-2 bg-amber-50 rounded-b-lg flex items-center justify-between gap-2">
                    <p className="text-xs text-amber-800">Prove this account is yours:</p>
                    <button
                      onClick={() => startOAuth(profile.platform)}
                      disabled={startingOAuth === profile.platform}
                      className="text-xs font-semibold text-indigo-700 hover:text-indigo-900 whitespace-nowrap disabled:opacity-60"
                    >
                      {startingOAuth === profile.platform ? 'Redirecting…' : `Sign in with ${PLATFORM_META[profile.platform]?.label}`}
                    </button>
                  </div>
                )}

                {postsFor === profile.platform && (
                  <div className="border-t border-gray-100 px-3 py-2 bg-gray-50 rounded-b-lg">
                    {postsLoading ? (
                      <div className="flex items-center gap-2 text-gray-500 text-xs py-1">
                        <Loader2 className="w-3 h-3 animate-spin" /> Loading activity…
                      </div>
                    ) : posts.length === 0 ? (
                      <p className="text-xs text-gray-500 py-1">No recent public activity.</p>
                    ) : (
                      <ul className="space-y-1.5 py-1">
                        {posts.slice(0, 5).map(post => (
                          <li key={post.id} className="text-xs text-gray-700">
                            {post.url ? (
                              <a href={post.url} target="_blank" rel="noopener noreferrer" className="hover:text-indigo-600">
                                {post.text}
                              </a>
                            ) : (
                              post.text
                            )}
                            <span className="text-gray-400"> · {new Date(post.createdAt).toLocaleDateString()}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {adding ? (
            <div className="mt-3">
              <p className="text-xs text-gray-500 mb-1.5">
                Paste your {PLATFORM_META[adding].label} profile URL. It will show as an
                <strong> unverified</strong> link{providers[adding] ? ' until you sign in to confirm ownership' : ''}.
              </p>
              <div className="flex gap-2">
                <input
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && linkByUrl(adding)}
                  placeholder={PLATFORM_META[adding].placeholder}
                  className="flex-1 min-w-0 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  autoFocus
                />
                <button
                  onClick={() => linkByUrl(adding)}
                  disabled={submitting}
                  className="px-3 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-60"
                >
                  {submitting ? 'Adding…' : 'Add'}
                </button>
              </div>
              <button
                onClick={() => { setAdding(null); setInputValue(''); }}
                className="mt-1.5 text-xs text-gray-500 hover:underline"
              >
                Cancel
              </button>
            </div>
          ) : (
            availablePlatforms.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {availablePlatforms.map(platform => (
                  <button
                    key={platform}
                    onClick={() =>
                      providers[platform]
                        ? startOAuth(platform)
                        : (() => { setAdding(platform); setInputValue(''); })()
                    }
                    disabled={startingOAuth === platform}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-200 text-xs font-medium text-gray-700 hover:border-indigo-300 hover:bg-indigo-50 transition-colors disabled:opacity-60"
                  >
                    {PLATFORM_META[platform].icon}
                    {startingOAuth === platform
                      ? 'Redirecting…'
                      : providers[platform]
                        ? `Sign in with ${PLATFORM_META[platform].label}`
                        : `Link ${PLATFORM_META[platform].label}`}
                  </button>
                ))}
              </div>
            )
          )}
        </>
      )}
    </div>
  );
};

export default SocialConnectionsCard;
